import { data, Link, redirect, useLoaderData } from 'react-router'
import { contentTokens } from '#app/ai/review-synthesis.ts'
import { resolveDistinctId } from '#app/analytics/attribution.server.ts'
import { type RouteAnalyticsHandle } from '#app/analytics/constants.ts'
import {
	BusinessListing,
	BusinessListingGroup,
} from '#app/components/business-listing.tsx'
import { DomainFavicon } from '#app/components/domain-favicon.tsx'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Card, CardContent } from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	RANKING_ELIGIBLE,
	getCategoryRankingStatus,
} from '#app/scoring/publishable-pages.server.ts'
import { SCORE_FACTORS } from '#app/scoring/scoring-model.ts'
import { type SeoProps } from '#app/seo/seo.tsx'
import { buildItemListLd } from '#app/seo/structured-data.ts'
import { recordListingImpressions } from '#app/utils/business-metrics.server.ts'
import { categoryPlural } from '#app/utils/category-name.ts'
import { prisma } from '#app/utils/db.server.ts'
import { PAGE_MEASURE, PAGE_PROSE } from '#app/utils/layout.ts'
import { createMemoCache } from '#app/utils/memo-cache.server.ts'
import { pluralize } from '#app/utils/misc.tsx'
import { type Route } from './+types/$categorySlug.ts'

/**
 * Cautions shown on a category hub, and how much topical overlap two of them
 * may share before the second is treated as a restatement of the first.
 */
const MAX_CAUTIONS = 4
const CAUTION_OVERLAP_MAX = 0.25

/**
 * Analyzed listings a topic must appear on before it is presented as
 * something reviewers flag about the category rather than about one business.
 */
const MIN_CAUTION_BUSINESSES = 2

/*
 * Every analyzed business in a category with its full watch-out text, which is
 * the heaviest read on this page and only changes when a synthesis runs.
 * Cached so a crawl of the category hubs does not pull it once per request.
 */
const getAnalyzedBusinesses = createMemoCache(
	'category watch-outs',
	10 * 60_000,
	(categoryId: string) =>
		prisma.business.findMany({
			where: {
				...RANKING_ELIGIBLE,
				categories: { some: { id: categoryId } },
				synthesisAt: { not: null },
			},
			select: { name: true, slug: true, watchOuts: true },
			orderBy: { score: 'desc' },
		}),
)

export async function loader({ params, request, context }: Route.LoaderArgs) {
	const { categorySlug } = params

	const [category, subcategory] = await Promise.all([
		prisma.category.findUnique({
			where: {
				slug: categorySlug,
				businesses: { some: { status: 'published' } },
			},
			select: {
				id: true,
				name: true,
				pluralName: true,
				slug: true,
				subcategories: {
					where: {
						businesses: { some: { status: 'published' } },
					},
					select: {
						id: true,
						name: true,
						slug: true,
						_count: {
							select: {
								businesses: { where: { status: 'published' } },
							},
						},
					},
					orderBy: {
						name: 'asc',
					},
				},
			},
		}),
		prisma.subcategory.findUnique({
			where: {
				slug: categorySlug,
				businesses: { some: { status: 'published' } },
			},
			select: {
				id: true,
				name: true,
				pluralName: true,
				slug: true,
				category: {
					select: {
						id: true,
						name: true,
						pluralName: true,
						slug: true,
						subcategories: {
							where: {
								businesses: { some: { status: 'published' } },
							},
							select: {
								id: true,
								name: true,
								slug: true,
								_count: {
									select: {
										businesses: { where: { status: 'published' } },
									},
								},
							},
							orderBy: {
								name: 'asc',
							},
						},
					},
				},
			},
		}),
	])

	if (!category && !subcategory) {
		throw new Response('Category not found', { status: 404 })
	}

	const effectiveCategory = subcategory ? subcategory.category : category

	/*
	 * Resolved before anything else is loaded.
	 *
	 * When the ranking publishes, it *is* the metro page for this category, and
	 * this hub is a second URL competing for the same query — "roofing
	 * contractors DFW" resolving to both /categories/{slug} and /best/{slug}.
	 * One has to win, and it should be the one carrying the ordered list, the
	 * claim evidence and the methodology.
	 *
	 * The check sits above the featured/shortlist/count/caution queries because
	 * every one of them is thrown away on a redirect, and for a publishable
	 * category that is every single request to this URL.
	 *
	 * Only for a category. A subcategory has no metro ranking of its own, so
	 * redirecting it would send "metal roofing" to the general roofing ranking
	 * and lose the distinction the URL exists to make.
	 *
	 * 301 rather than a canonical tag: these are the same page for the reader
	 * too, and a redirect stops the duplicate being crawled at all rather than
	 * asking a crawler to discount it after the fact.
	 */
	const rankingStatus = await getCategoryRankingStatus(effectiveCategory!.id)

	if (!subcategory && rankingStatus.publishes) {
		throw redirect(`/best/${effectiveCategory!.slug}`, { status: 301 })
	}

	const subcategories = effectiveCategory?.subcategories || []

	const baseWhere = subcategory
		? { subcategories: { some: { id: subcategory.id } }, status: 'published' }
		: {
				categories: { some: { id: effectiveCategory!.id } },
				status: 'published',
			}

	// Get featured business (DFW Pick)
	const featuredBusiness = await prisma.business.findFirst({
		where: {
			...baseWhere,
			isFeatured: true,
		},
		select: {
			id: true,
			name: true,
			slug: true,
			description: true,
			aiDescription: true,
			phone: true,
			website: true,
			highlights: true,
			cities: {
				select: { id: true, name: true, slug: true },
				take: 3,
			},
		},
		orderBy: { createdAt: 'desc' },
	})

	/*
	 * The sample shown on this hub.
	 *
	 * Ordered by score, and deliberately not by `isFeatured`. It used to lead
	 * with the featured flag, which meant a paid or hand-picked business sat
	 * above better-scoring ones in a visible ordering — the one thing
	 * `llms.txt` publicly promises cannot happen ("no business can pay for a
	 * score, a rank, or the removal of an accurate caution").
	 *
	 * The invariant, which holds across the site: no query that produces an
	 * ordering may read `isFeatured`. A featured business still appears here,
	 * and still ranks in /best/, at whatever position it earned. The paid slot
	 * is the labeled block above this list, outside the ordering entirely.
	 *
	 * Unscored businesses sort last rather than being hidden. Absence of a
	 * score is not a demerit, but it cannot outrank a measured one either.
	 */
	const shortlistBusinesses = await prisma.business.findMany({
		where: baseWhere,
		select: {
			id: true,
			name: true,
			slug: true,
			description: true,
			aiDescription: true,
			// The shared row leads with measurement rather than a favicon.
			score: true,
			ratingValue: true,
			ratingCount: true,
			bestFor: true,
			phone: true,
			website: true,
			isFeatured: true,
			highlights: true,
			cities: {
				select: { id: true, name: true, slug: true },
				take: 2,
			},
		},
		orderBy: [
			{ score: { sort: 'desc', nulls: 'last' } },
			{ createdAt: 'desc' },
		],
		take: 6,
	})

	// Get total count
	const total = await prisma.business.count({ where: baseWhere })

	/*
	 * Only reachable on a subcategory hub, or a category whose ranking is not
	 * publishable — the redirect above has already returned for every other
	 * case. A subcategory links up to its parent's ranking when one exists.
	 */
	const ranking = rankingStatus.publishes
		? {
				slug: effectiveCategory!.slug,
				name: effectiveCategory!.name,
				// Carried so the CTA reads "the 12 highest-scoring pest control in
				// DFW" rather than "pest controls".
				pluralName: effectiveCategory!.pluralName,
				count: rankingStatus.total,
			}
		: null

	/*
	 * Recurring cautions in this category, taken from synthesized review
	 * corpora.
	 *
	 * This replaces four hand-written "DFW-specific red flags" cards that were
	 * rendered for every category regardless of what it was: an accounting hub
	 * warned readers about subcontractor churn and seasonal roofing pricing.
	 * The invented version could not be true for 106 categories, and inventing
	 * a per-category equivalent would manufacture risks we have no evidence for
	 * — exactly what the synthesis citation gate exists to prevent.
	 *
	 * These come from claims that already cleared that gate, so each one is
	 * backed by separate reviews of a real business.
	 *
	 * The section claims a *category pattern*, so recurrence is the selection
	 * criterion, not presentation. An earlier version took the first topically
	 * distinct caution from each of the top-scoring listings, which produced a
	 * page that said "what reviewers flag about {category} in DFW" above four
	 * claims that might each have come from a single business. Topics are now
	 * clustered across every analyzed listing and only those appearing on at
	 * least MIN_CAUTION_BUSINESSES of them survive, ordered by how many.
	 *
	 * Every analyzed business is considered, not the top 20 by score: a pattern
	 * that only shows up outside the leaders is still a pattern, and sampling
	 * the winners would bias the category's cautions optimistically.
	 */
	const analyzedBusinesses = await getAnalyzedBusinesses(effectiveCategory!.id)

	/*
	 * Clustered on topical vocabulary rather than exact text: four businesses
	 * whose reviews all mention scheduling delays produce four differently
	 * worded sentences about the same thing, and counting them as four separate
	 * topics would both understate the pattern and pad the section.
	 *
	 * contentTokens is the same vocabulary the theme grouper uses, reused
	 * rather than restated so this page cannot develop its own idea of what two
	 * cautions having "the same subject" means.
	 */
	interface CautionCluster {
		tokens: Set<string>
		/** Shortest phrasing seen, which is reliably the most general. */
		text: string
		businesses: Set<string>
		example: { business: string; slug: string }
	}

	const clusters: CautionCluster[] = []

	for (const business of analyzedBusinesses) {
		// A business mentioning one topic twice is still one business for it.
		const countedHere = new Set<CautionCluster>()

		for (const text of business.watchOuts) {
			const tokens = contentTokens(text)
			if (tokens.size === 0) continue

			/*
			 * Containment against the shorter claim, not a raw count.
			 *
			 * contentTokens was built for short themes, so on full sentences it
			 * lets prose filler through ("some", "while", "than"). That inflates
			 * both sets and would let two sentences about the same subject look
			 * distinct purely because they are wordy. Measuring the shared
			 * fraction of the smaller claim is stable against that padding.
			 *
			 * The threshold was fitted against the pilot corpus rather than
			 * guessed: at 0.35 and above the section filled with differently
			 * worded scheduling complaints, at 0.25 it separates scheduling,
			 * insurance claims, estimate transparency and site cleanup.
			 */
			const match = clusters.find((cluster) => {
				const shared = [...tokens].filter((t) => cluster.tokens.has(t)).length
				return (
					shared / Math.min(tokens.size, cluster.tokens.size) >=
					CAUTION_OVERLAP_MAX
				)
			})

			if (match) {
				if (countedHere.has(match)) continue
				countedHere.add(match)
				match.businesses.add(business.slug)
				/*
				 * The displayed sentence and the business credited with it move
				 * together. Adopting a shorter phrasing while leaving `example` on
				 * the cluster's seed would print one business's wording under
				 * another's name, and the reader who clicked through to check would
				 * not find that sentence on the listing.
				 */
				if (text.length < match.text.length) {
					match.text = text
					match.example = { business: business.name, slug: business.slug }
				}
				continue
			}

			const cluster: CautionCluster = {
				tokens,
				text,
				businesses: new Set([business.slug]),
				example: { business: business.name, slug: business.slug },
			}
			clusters.push(cluster)
			countedHere.add(cluster)
		}
	}

	const cautions = clusters
		.filter((cluster) => cluster.businesses.size >= MIN_CAUTION_BUSINESSES)
		.sort((a, b) => b.businesses.size - a.businesses.size)
		.slice(0, MAX_CAUTIONS)
		.map((cluster) => ({
			text: cluster.text,
			businessCount: cluster.businesses.size,
			example: cluster.example,
		}))

	const analyzedCount = analyzedBusinesses.length

	// Get cities that have businesses in this category
	const citiesWithCategory = await prisma.city.findMany({
		where: {
			businesses: {
				some: baseWhere,
			},
		},
		select: {
			id: true,
			name: true,
			slug: true,
			_count: {
				select: {
					businesses: {
						where: baseWhere,
					},
				},
			},
		},
		orderBy: { name: 'asc' },
	})

	// Same treatment as the other discovery surfaces: counted server-side,
	// bot-filtered, deduped per visitor, and never awaited.
	void recordListingImpressions({
		request,
		// Resolved rather than left to the cookie: on a cookieless first hit the
		// engagement guard would otherwise key this visitor on IP+UA, which is a
		// coarser identity than the one search already uses.
		distinctId: resolveDistinctId({ request, context }),
		businessIds: shortlistBusinesses.map((b) => b.id),
	})

	return data({
		category: effectiveCategory,
		subcategory,
		subcategories,
		featuredBusiness,
		shortlistBusinesses,
		citiesWithCategory,
		total,
		ranking,
		cautions,
		analyzedCount,
	})
}

export const handle = {
	// Read by useRouteAnalytics in root.tsx; tags every pageview and
	// client event fired on this route with its page archetype.
	analytics: { pageType: 'category_hub' } satisfies RouteAnalyticsHandle,
	breadcrumb({ data }: { data: Awaited<ReturnType<typeof loader>>['data'] }) {
		const entity = data?.subcategory ?? data?.category
		return entity
			? { name: categoryPlural(entity), url: `/categories/${entity.slug}` }
			: null
	},
	seo({
		loaderData,
		origin,
	}: {
		loaderData: Awaited<ReturnType<typeof loader>>['data']
		origin: string
	}): SeoProps | null {
		const entity = loaderData?.subcategory ?? loaderData?.category
		if (!entity) return null
		const canonical = `${origin}/categories/${entity.slug}`

		const structuredData =
			loaderData.shortlistBusinesses.length > 0
				? [
						buildItemListLd({
							name: `${categoryPlural(entity)} in Dallas-Fort Worth`,
							items: loaderData.shortlistBusinesses.map((b) => ({
								name: b.name,
								url: `${origin}/business/${b.slug}`,
							})),
						}),
					]
				: undefined

		/*
		 * "Vetted", "honest recommendations" and "we research the market so you
		 * don't have to" all described human editorial work that does not happen.
		 * The count is the claim now, and it is one we can defend.
		 */
		const title = `${categoryPlural(entity)} in Dallas-Fort Worth | DFW Professionals`
		const description = `${loaderData.total.toLocaleString()} ${categoryPlural(entity).toLowerCase()} across Dallas-Fort Worth, scored against a published rubric on what reviewers wrote rather than the star they clicked. Sponsored placements are labeled and excluded.`

		return {
			title,
			description,
			canonical,
			openGraph: { title, description, type: 'website', url: canonical },
			twitter: { card: 'summary_large_image', title, description },
			structuredData,
		}
	},
}

export default function CategoryHubPage() {
	const {
		category,
		subcategory,
		subcategories,
		featuredBusiness,
		shortlistBusinesses,
		citiesWithCategory,
		total,
		ranking,
		cautions,
		analyzedCount,
	} = useLoaderData<typeof loader>()

	if (!category) {
		throw new Error('Category is required')
	}

	const entity = subcategory ?? category
	const entityNamePlural = categoryPlural(entity)
	const entityNameLower = entity.name.toLowerCase()

	return (
		<>
			{/* ═══════════════════════════════════════════════════════════════════
			    HERO SECTION — Editorial Guide + Context
			    ═══════════════════════════════════════════════════════════════════ */}
			<section className="pt-16 pb-12 md:pt-24 md:pb-16">
				<div className={PAGE_MEASURE}>
					{/* Breadcrumb context */}
					<div className="text-muted-foreground mb-6 flex items-center gap-2 text-sm">
						<Link to="/categories" className="hover:text-foreground">
							All Categories
						</Link>
						<Icon name="chevron-right" className="size-4" />
						<span className="text-foreground">{entityNamePlural}</span>
					</div>

					<h1 className="mb-4 font-serif text-3xl tracking-tight md:text-4xl">
						{entityNamePlural} in Dallas-Fort Worth
					</h1>

					{/*
					 * Said the wrong choice "costs time, stress, and sometimes your
					 * trust in the process" and that we "research the DFW market" for
					 * the reader. The first is filler that helps nobody choose; the
					 * second describes work no human does here.
					 */}
					<p
						className={`text-muted-foreground mb-6 text-lg leading-relaxed ${PAGE_PROSE}`}
					>
						{total.toLocaleString()} {entityNameLower}
						{total === 1 ? '' : 's'} across Dallas-Fort Worth. Scores come from
						reading the public reviews each has already received, against a
						rubric published in full.
					</p>

					{/*
					 * The ranking is the deepest page this category has, and the hub is
					 * where a reader lands from nav and internal links. Offering it
					 * first here is the difference between /best/ having one inbound
					 * internal link and having one from every category surface.
					 */}
					{ranking ? (
						<div className="mb-6">
							<Button asChild size="lg">
								<Link to={`/best/${ranking.slug}`}>
									<Icon name="chart-no-axes-column" className="mr-2 size-4" />
									See the {ranking.count} highest-scoring{' '}
									{categoryPlural(ranking).toLowerCase()} in DFW
								</Link>
							</Button>
							<p className="text-muted-foreground mt-2 text-sm">
								Ranked on review sentiment, rating, volume and recency.{' '}
								<Link
									className="underline underline-offset-4"
									to="/scoring-method"
								>
									How we score
								</Link>
							</p>
						</div>
					) : null}

					{/* Quick stats */}
					<div className="flex flex-wrap gap-4 text-sm">
						<div className="flex items-center gap-2">
							<Icon name="check" className="size-4 text-green-600" />
							<span>
								{total} provider{total !== 1 ? 's' : ''} researched
							</span>
						</div>
						<div className="flex items-center gap-2">
							<Icon name="map-pin" className="text-muted-foreground size-4" />
							<span>
								{citiesWithCategory.length} DFW{' '}
								{citiesWithCategory.length === 1 ? 'city' : 'cities'}
							</span>
						</div>
					</div>
				</div>
			</section>

			{/* ═══════════════════════════════════════════════════════════════════
			    SUBCATEGORIES — Prominent placement for niche discovery & SEO
			    ═══════════════════════════════════════════════════════════════════ */}
			{subcategories.length > 0 && !subcategory && (
				<section className="bg-muted/30 border-y py-10 md:py-12">
					<div className={PAGE_MEASURE}>
						<div>
							<div className="mb-6 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
								<div>
									<h2 className="text-lg font-medium">
										Looking for something specific?
									</h2>
									<p className="text-muted-foreground text-sm">
										Browse {entityNamePlural.toLowerCase()} by specialty
									</p>
								</div>
							</div>

							<div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
								{subcategories.map((sub) => (
									<Link
										key={sub.id}
										to={`/categories/${sub.slug}`}
										className="group"
									>
										<Card className="h-full transition-all hover:shadow-md">
											<CardContent className="flex items-center justify-between p-4">
												<div>
													<h3 className="text-sm font-medium group-hover:underline">
														{sub.name}
													</h3>
													<p className="text-muted-foreground text-xs">
														{sub._count.businesses} provider
														{sub._count.businesses !== 1 ? 's' : ''}
													</p>
												</div>
												<Icon
													name="chevron-right"
													className="text-muted-foreground size-4 transition-transform group-hover:translate-x-0.5"
												/>
											</CardContent>
										</Card>
									</Link>
								))}
							</div>
						</div>
					</div>
				</section>
			)}

			{/* ═══════════════════════════════════════════════════════════════════
			    DFW PICK — Featured Business
			    ═══════════════════════════════════════════════════════════════════ */}
			{featuredBusiness && (
				<section className="bg-muted/30 border-y py-12 md:py-16">
					<div className={PAGE_MEASURE}>
						<div className={PAGE_PROSE}>
							<div className="mb-6 flex items-center gap-3">
								{/*
								 * Labeled as a placement, because that is what it is: this
								 * business is selected by the isFeatured flag, not by score.
								 * "DFW Pick, our featured recommendation" presented an
								 * unearned slot as an earned judgment, and a ranking is only
								 * citable while that difference stays visible on the page.
								 *
								 * Deliberately does not say "paid": nothing in the schema
								 * records payment, so asserting it would be its own invented
								 * claim. It also does not say "excluded from the ranking",
								 * which would be false. isFeatured has no bearing on ranking
								 * eligibility, and a featured business carrying a score is
								 * ranked on it like any other. `excludeFromRankings` is the
								 * separate flag that would make an exclusion claim true.
								 */}
								<Badge variant="outline">Featured</Badge>
								<span className="text-muted-foreground text-sm">
									Chosen by us, not by score.
								</span>
							</div>

							<Card>
								<CardContent className="p-6 md:p-8">
									<div className="flex items-start gap-4">
										{featuredBusiness.website && (
											<DomainFavicon
												domain={featuredBusiness.website}
												size={40}
												className="shrink-0 rounded-lg"
											/>
										)}
										<div className="flex-1">
											<h3 className="mb-2 text-xl font-semibold">
												{featuredBusiness.name}
											</h3>
											{featuredBusiness.cities.length > 0 && (
												<p className="text-muted-foreground mb-3 text-sm">
													Serving{' '}
													{featuredBusiness.cities
														.map((c) => c.name)
														.join(', ')}
												</p>
											)}
											<p className="text-muted-foreground mb-5 leading-relaxed">
												{featuredBusiness.aiDescription ||
													featuredBusiness.description}
											</p>

											{featuredBusiness.highlights.length > 0 && (
												<div className="mb-6">
													<p className="mb-2 text-sm font-medium">
														What this business says about itself:
													</p>
													<ul className="space-y-1.5">
														{featuredBusiness.highlights
															.slice(0, 3)
															.map((highlight, idx) => (
																<li
																	key={idx}
																	className="flex items-start gap-2 text-sm"
																>
																	<Icon
																		name="check"
																		className="mt-0.5 size-4 shrink-0 text-green-600"
																	/>
																	<span className="text-muted-foreground">
																		{highlight}
																	</span>
																</li>
															))}
													</ul>
												</div>
											)}

											<div className="flex flex-wrap gap-3">
												{featuredBusiness.phone && (
													<Button asChild>
														<a href={`tel:${featuredBusiness.phone}`}>
															<Icon name="phone" className="mr-2 size-4" />
															Call Now
														</a>
													</Button>
												)}
												<Button variant="outline" asChild>
													<Link to={`/business/${featuredBusiness.slug}`}>
														Read our reasoning
														<Icon name="arrow-right" className="ml-2 size-4" />
													</Link>
												</Button>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</div>
					</div>
				</section>
			)}

			{/* ═══════════════════════════════════════════════════════════════════
			    WHAT REVIEWERS FLAG — derived from synthesized review corpora.
			    Renders nothing when the category has no grounded cautions yet,
			    which is the correct output for a category we have not analyzed.
			    ═══════════════════════════════════════════════════════════════════ */}
			{cautions.length > 0 ? (
				<section className="py-12 md:py-16">
					<div className={PAGE_MEASURE}>
						<h2 className="mb-3 font-serif text-2xl tracking-tight">
							What reviewers actually flag about {entityNamePlural} in DFW
						</h2>
						<p
							className={`text-muted-foreground mb-8 leading-relaxed ${PAGE_PROSE}`}
						>
							Each of these came up in the reviews of more than one of the{' '}
							{analyzedCount} {entityNamePlural.toLowerCase()} we have analyzed
							in depth. Every caution is supported by separate reviews of the
							businesses it applies to, and appears on those listings with the
							source reviews attached.
						</p>

						<div className="grid gap-4 md:grid-cols-2">
							{cautions.map((caution) => (
								/*
								 * Keyed on the caution itself, not the example business: one
								 * listing can seed two distinct topics that both survive the
								 * frequency filter, and keying on its slug would render two
								 * cards with the same key. Two clusters cannot hold identical
								 * text — identical text would have matched and merged.
								 */
								<div
									key={caution.text}
									className="bg-background rounded-lg border p-5"
								>
									<div className="bg-destructive/10 text-destructive mb-3 flex size-10 items-center justify-center rounded-full">
										<Icon name="exclamation-triangle" className="size-5" />
									</div>
									<p className="text-muted-foreground mb-3 text-sm leading-relaxed">
										{caution.text}
									</p>
									<p className="text-muted-foreground text-xs">
										Raised on {caution.businessCount} of {analyzedCount}{' '}
										analyzed listings, including{' '}
										<Link
											to={`/business/${caution.example.slug}`}
											className="font-medium underline underline-offset-4"
										>
											{caution.example.business}
										</Link>
									</p>
								</div>
							))}
						</div>
					</div>
				</section>
			) : null}

			{/* ═══════════════════════════════════════════════════════════════════
			    DFW SHORTLIST
			    ═══════════════════════════════════════════════════════════════════ */}
			{shortlistBusinesses.length > 0 && (
				<section className="bg-muted/30 border-y py-12 md:py-16">
					<div className={PAGE_MEASURE}>
						<div>
							<div className="mb-8">
								<h2 className="mb-3 font-serif text-2xl tracking-tight">
									{entityNamePlural} in Dallas-Fort Worth
								</h2>
								{/*
								 * Says what this sample is. It claimed each business had "been
								 * reviewed for responsiveness, reputation, and local track
								 * record" — no human reviewed any of them, and two of those
								 * three are not factors in the rubric.
								 */}
								<p className="text-muted-foreground max-w-xl">
									{shortlistBusinesses.length} of the {total.toLocaleString()}{' '}
									businesses we hold in this category. Scores, where shown, come
									from the published rubric.
								</p>
							</div>

							<BusinessListingGroup>
								{shortlistBusinesses.map((business, index) => (
									<li key={business.id}>
										<BusinessListing
											business={business}
											track={{
												pageType: 'category_hub',
												categorySlug: category.slug,
												position: index + 1,
											}}
										/>
									</li>
								))}
							</BusinessListingGroup>

							{total > shortlistBusinesses.length && (
								<p className="text-muted-foreground mt-8 text-center text-sm">
									Showing {shortlistBusinesses.length} of {total} providers.{' '}
									<a
										href="#browse-by-city"
										className="hover:text-foreground underline"
									>
										Browse by city
									</a>{' '}
									for location-specific recommendations.
								</p>
							)}
						</div>
					</div>
				</section>
			)}

			{/* ═══════════════════════════════════════════════════════════════════
			    BROWSE BY CITY
			    ═══════════════════════════════════════════════════════════════════ */}
			{citiesWithCategory.length > 0 && (
				<section id="browse-by-city" className="py-12 md:py-16">
					<div className={PAGE_MEASURE}>
						<div className="mb-8">
							<h2 className="mb-3 font-serif text-2xl tracking-tight">
								{entityNamePlural} by city
							</h2>
							{/*
							 * Promised "response times, common issues" on every city page.
							 * Neither is measured, and neither is rendered there.
							 */}
							<p className="text-muted-foreground max-w-xl">
								Each city is scored only over the businesses that serve it, so
								the order differs from the metro-wide one.
							</p>
						</div>

						<div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
							{citiesWithCategory.map((city) => (
								<Link
									key={city.id}
									to={`/${city.slug}/${subcategory?.slug ?? category.slug}`}
									className="group"
								>
									<Card className="transition-all hover:shadow-md">
										<CardContent className="flex items-center justify-between p-4">
											<div>
												<h3 className="font-medium group-hover:underline">
													{city.name}
												</h3>
												<p className="text-muted-foreground text-sm">
													{city._count.businesses} provider
													{city._count.businesses !== 1 ? 's' : ''}
												</p>
											</div>
											<Icon
												name="arrow-right"
												className="text-muted-foreground size-4 transition-transform group-hover:translate-x-1"
											/>
										</CardContent>
									</Card>
								</Link>
							))}
						</div>
					</div>
				</section>
			)}

			{/* ═══════════════════════════════════════════════════════════════════
			    DFW-WIDE FAQ
			    ═══════════════════════════════════════════════════════════════════ */}
			<section className="bg-muted/30 border-y py-12 md:py-16">
				<div className={PAGE_MEASURE}>
					<div className={PAGE_PROSE}>
						<h2 className="mb-8 font-serif text-2xl tracking-tight">
							Common questions
						</h2>

						{/*
						 * Was five answers of market commentary rendered across every
						 * category: that DFW demand "outpaces supply", that wait times run
						 * days to weeks, that fee variation signals corner-cutting. None of
						 * it is measured, and seasonal-availability claims are simply false
						 * for most of the ~106 categories this file renders.
						 *
						 * One answer told readers to "look beyond star ratings" and judge by
						 * how confidently a business answers the phone, which is both
						 * unmeasurable and a direct argument against the page it sits on.
						 */}
						<Accordion type="single" collapsible className="w-full">
							<AccordionItem value="ordering">
								<AccordionTrigger>How is this list ordered?</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									Software reads the public Google reviews each business has
									already received and scores them against{' '}
									{SCORE_FACTORS.length} published, weighted factors. The
									heaviest weight is on what reviewers wrote, not the star they
									clicked. Every weight is listed at{' '}
									<Link
										className="underline underline-offset-4"
										to="/scoring-method"
									>
										how we score
									</Link>
									.
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="paid">
								<AccordionTrigger>
									Can a business pay to appear higher?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									No. Rank comes from the rubric and nothing else. Anything
									featured or sponsored is labeled as such wherever it appears.
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="verified">
								<AccordionTrigger>
									Have you verified these businesses?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									Not in the sense most directories imply. We read public
									reviews with software. Nobody interviews customers, inspects
									work or checks a licence, and a high score is not a
									credential. Where Texas licenses this trade, verify the
									licence yourself with the relevant state board before hiring.
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="missing">
								<AccordionTrigger>
									Why is a business I know missing?
								</AccordionTrigger>
								<AccordionContent className="text-muted-foreground leading-relaxed">
									Most likely we do not hold a record for it yet, or it has too
									few public reviews to score without the result being noise.
									Absence is not a judgment. You can{' '}
									<Link className="underline underline-offset-4" to="/submit">
										submit it
									</Link>
									.
								</AccordionContent>
							</AccordionItem>
						</Accordion>
					</div>
				</div>
			</section>

			{/* ═══════════════════════════════════════════════════════════════════
			    RELATED SUBCATEGORIES — When viewing a subcategory
			    ═══════════════════════════════════════════════════════════════════ */}
			{subcategory && subcategories.length > 1 && (
				<section className="py-12 md:py-16">
					<div className={PAGE_MEASURE}>
						<h2 className="mb-2 text-lg font-medium">
							Related {categoryPlural(category).toLowerCase()} services
						</h2>
						<p className="text-muted-foreground mb-6 text-sm">
							Looking for a different specialty within {category.name}?
						</p>
						<div className="flex flex-wrap gap-2">
							{subcategories
								.filter((sub) => sub.id !== subcategory.id)
								.map((sub) => (
									<Link key={sub.id} to={`/categories/${sub.slug}`}>
										<Badge
											variant="outline"
											className="hover:bg-accent hover:text-accent-foreground cursor-pointer px-3 py-1.5 transition-colors"
										>
											{sub.name}
											<span className="text-muted-foreground ml-1">
												({sub._count.businesses})
											</span>
										</Badge>
									</Link>
								))}
							<Link to={`/categories/${category.slug}`}>
								<Badge
									variant="secondary"
									className="cursor-pointer px-3 py-1.5 transition-colors"
								>
									View all {categoryPlural(category)} →
								</Badge>
							</Link>
						</div>
					</div>
				</section>
			)}

			{/* ═══════════════════════════════════════════════════════════════════
			    FINAL CTA
			    ═══════════════════════════════════════════════════════════════════ */}
			<section className="border-t py-12 md:py-16">
				<div className={PAGE_MEASURE}>
					<div className={PAGE_PROSE}>
						<h2 className="mb-4 font-serif text-xl tracking-tight">
							Missing a {entityNameLower} you know?
						</h2>
						{/*
						 * A submission adds a business to be scored like any other. It is
						 * not a nomination and carries no weight in the rubric. Asking for
						 * "recommendations from residents who've had good experiences"
						 * implied reader sentiment feeds rank, which would make the whole
						 * ordering look negotiable.
						 */}
						<p className="text-muted-foreground mb-6">
							Submit it and it will be scored against the same rubric as
							everything else here. Submissions do not affect rank.
						</p>
						<Button variant="outline" asChild>
							<Link to="/submit">
								<Icon name="plus" className="mr-2 size-4" />
								Submit a business
							</Link>
						</Button>
					</div>
				</div>
			</section>
		</>
	)
}
